// src/components/maps/DoctorMap.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import MapGL, { Marker, Source, Layer } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_STYLE_LIGHT } from "@/constants/mapStyles.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const MAP_READY_TIMEOUT_MS = 15000;
const MAX_MAP_RECOVERY_ATTEMPTS = 1;
const MAP_DIAGNOSTIC_LIMIT = 40;

function getMapboxInstance(mapLike) {
  if (!mapLike) return null;
  if (typeof mapLike.getMap === "function") return mapLike.getMap();
  return mapLike;
}

function sanitiseMapMessage(value) {
  return String(value || "")
    .replace(/https?:\/\/[^\s)]+/gi, "[resource]")
    .replace(/access_token=[^&\s)]+/gi, "access_token=[redacted]")
    .replace(/[A-Za-z0-9_-]{20,}/g, "[redacted]")
    .slice(0, 180);
}

function classifyMapError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  if (!message) return "unknown";
  if (/access token|token.*(invalid|authori[sz]ed)|not authorised/.test(message)) return "token";
  if (/style/.test(message)) return "style";
  if (/sprite/.test(message)) return "sprite";
  if (/glyph/.test(message)) return "glyph";
  if (/tile|source/.test(message)) return "tile";
  if (/content security policy|csp|blocked/.test(message)) return "resource_blocked";
  if (/worker/.test(message)) return "worker";
  if (/webgl|context/.test(message)) return "webgl";
  if (/network|offline|fetch/.test(message)) return "network";
  return "unknown";
}

function readSafeMapRuntime() {
  if (typeof window === "undefined") return {};
  return {
    online: navigator.onLine !== false,
    visibility: document.visibilityState,
    viewport: `${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)}`,
    devicePixelRatio: Number(window.devicePixelRatio || 1),
    userAgent: String(navigator.userAgent || "").slice(0, 220),
  };
}

function recordMapDiagnostic(surface, stage, detail = {}) {
  if (typeof window === "undefined") return;
  const record = {
    at: new Date().toISOString(),
    surface: String(surface || "doctor-map"),
    stage: String(stage || "unknown"),
    ...detail,
  };
  const buffer = Array.isArray(window.__AFRIMED_MAP_DIAGNOSTICS__)
    ? window.__AFRIMED_MAP_DIAGNOSTICS__
    : [];
  buffer.push(record);
  window.__AFRIMED_MAP_DIAGNOSTICS__ = buffer.slice(-MAP_DIAGNOSTIC_LIMIT);
  if (import.meta.env.DEV) {
    console.info("[AfriMed map]", record);
  }
}

function MapStatus({ state, onRetry, t }) {
  const isUnsupported = state === "unsupported";
  const isLoading = state === "checking" || state === "loading";
  const title = isUnsupported
    ? t("maps.unsupported.title", "Interactive map unsupported")
    : isLoading
      ? t("maps.loading.title", "Loading map")
      : t("maps.unavailable.title", "Map temporarily unavailable");
  const body = isUnsupported
    ? t("maps.unsupported.body", "You can still browse the healthcare results below.")
    : isLoading
      ? t("maps.loading.body", "Preparing healthcare locations…")
      : t("maps.unavailable.body", "You can still browse the healthcare results below. Try loading the map again.");

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-[rgb(var(--clr-surface))] px-6 text-center text-on-surface"
      role={isLoading ? "status" : "alert"}
      aria-live="polite"
      data-testid="doctor-map-status"
    >
      <div className="text-body-large font-medium">{title}</div>
      <p className="max-w-sm text-body-medium text-on-surface-var">{body}</p>
      {!isLoading && !isUnsupported ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-[12px] bg-[rgb(var(--afrimed-primary))] px-4 py-2 text-body-medium font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--afrimed-primary))]"
        >
          {t("maps.retry", "Try again")}
        </button>
      ) : null}
    </div>
  );
}

export default function DoctorMap({
  center, // { lat, lng }
  user = null, // { lat, lng } (optional)
  showUser = false,
  doctors = [],
  facilities = [],

  heightClass = "h-80",
  mapStyle = MAPBOX_STYLE_LIGHT,

  doctorIcon = "/markers/afrimed-nearest-doctor.svg",
  doctorIconNearest = "/markers/afrimed-nearest-doctor.svg",
  facilityIcon = "/markers/afrimed-hospital.svg",
  facilityIconNearest = "/markers/afrimed-nearest-hospital.svg",
  userIcon = "/markers/afrimed-user.svg",

  facilitySize = 44,
  facilityNearestSize = 64,
  doctorSize = 44,
  doctorNearestSize = 56,
  userSize = 32,

  frameClassName = "w-full overflow-hidden rounded-[12px] border border-[rgba(91,107,134,0.20)]",

  onMapReady,
  onBoundsChange = null,
  highlightFacilityId = null,
  focusTarget = null,

  // tap facility marker → parent decides navigation
  onFacilitySelect = null,
  onFacilityPopupClose = null,
  onRequireLocation = null,
  diagnosticSurface = "doctor-map",
}) {
  const { t } = useLanguage();
  const token = import.meta.env.VITE_MAPBOX_TOKEN;
  const frameRef = useRef(null);
  const mapRef = useRef(null);
  const watchdogRef = useRef(null);
  const recoveryRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const mapListenerCleanupRef = useRef(() => {});
  const recoveryAttemptsRef = useRef(0);
  const mapReachedRenderRef = useRef(false);
  const mapReachedIdleRef = useRef(false);
  const mapReadyRef = useRef(false);
  const mapStartedAtRef = useRef(0);
  const MAX_VISIBLE_FACILITY_MARKERS = 260;
  const MAX_VISIBLE_DOCTOR_MARKERS = 120;

  const [popup, setPopup] = useState(null);
  const activeMapPopup = popup;
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapSupport, setMapSupport] = useState("checking");
  const [mapState, setMapState] = useState("checking");
  const [mapInstanceKey, setMapInstanceKey] = useState(0);
  const closingPopupIdRef = useRef(null);
  const suppressNextMapClickRef = useRef(false);
  const [isDesktopMap, setIsDesktopMap] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );

  const isValidLatLng = useCallback((lat, lng) => {
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
    if (la < -90 || la > 90 || lo < -180 || lo > 180) return false;
    // Common placeholder coordinate in imported datasets.
    if (Math.abs(la) < 0.000001 && Math.abs(lo) < 0.000001) return false;
    return true;
  }, []);

  const normalizeFacilityName = useCallback((value) => {
    const raw = String(
      value?.name ||
      value?.facility_name ||
      value?.title ||
      ""
    ).trim();
    if (!raw || /^location$/i.test(raw)) return "Healthcare facility";
    return raw;
  }, []);

  const normalizeFacilityAddress = useCallback((value) => {
    const line1Raw = String(
      value?.address ||
      value?.facility_address ||
      value?.street_address ||
      ""
    ).trim();
    const city = String(value?.city || value?.facility_city || "").trim();
    const country = String(value?.country || value?.facility_country || "").trim();
    const line2 = [city, country].filter(Boolean).join(", ");

    const line1 = /^location$/i.test(line1Raw) || /^near\s+/i.test(line1Raw)
      ? ""
      : line1Raw;

    const normalizeLocationChunk = (input) =>
      String(input || "")
        .toLowerCase()
        .replace(/[·|]/g, ",")
        .replace(/[\s\-_/]+/g, " ")
        .replace(/\s*,\s*/g, ",")
        .replace(/\s+/g, " ")
        .trim();

    const normalizedLine1 = normalizeLocationChunk(line1);
    const normalizedLine2 = normalizeLocationChunk(line2);
    const line1AlreadyContainsLine2 =
      !!normalizedLine1 &&
      !!normalizedLine2 &&
      (normalizedLine1.includes(normalizedLine2) ||
        normalizedLine2.split(",").every((part) => part && normalizedLine1.includes(part)));

    return [line1, line1AlreadyContainsLine2 ? "" : line2].filter(Boolean).join(" · ");
  }, []);

// -------------------------------
// Reverse-geocode (Mapbox) cache
// -------------------------------
const geocodeCacheRef = useRef(new Map());

function cacheKey(lat, lng) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return "";
  // 5dp is plenty + stable keys
  return `${la.toFixed(5)},${lo.toFixed(5)}`;
}

async function reverseGeocodePlaceName(lat, lng) {
  const key = cacheKey(lat, lng);
  if (!key) return "";

  // cache hit
  const cached = geocodeCacheRef.current.get(key);
  if (typeof cached === "string") return cached;

  // cache "in-flight" promise to avoid duplicates
  if (cached && typeof cached.then === "function") {
    try {
      return await cached;
    } catch {
      return "";
    }
  }

  const token0 = token; // uses your existing VITE_MAPBOX_TOKEN
  if (!token0) return "";

  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
    `${encodeURIComponent(String(lng))},${encodeURIComponent(String(lat))}.json` +
    `?access_token=${encodeURIComponent(token0)}` +
    `&limit=1&types=address,poi,place`;

  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return "";
      const json = await res.json();
      const place = json?.features?.[0]?.place_name || "";
      return String(place || "").trim();
    } catch {
      return "";
    }
  })();

  geocodeCacheRef.current.set(key, p);

  const place = await p;
  geocodeCacheRef.current.set(key, place || "");
  return place || "";
}

async function hydratePopupAddress(payload) {
  try {
    const lat = payload?.lat;
    const lng = payload?.lng;
    if (lat == null || lng == null) return;

    // if we already have an address, don’t overwrite
    const existing = String(payload?.address || "").trim();
    if (existing) return;

    const place = await reverseGeocodePlaceName(lat, lng);
    if (!place) return;

    // only update if popup is still the same point and still missing address
    setPopup((prev) => {
      if (!prev) return prev;
      const same =
        Number(prev.lat) === Number(lat) && Number(prev.lng) === Number(lng);
      if (!same) return prev;
      if (String(prev.address || "").trim()) return prev;
      return { ...prev, address: place };
    });
  } catch {
    // 🔒 silent by design (no red errors)
  }
}

  // ✅ Directions state (in-app, Mapbox Directions API)
  const [routeGeo, setRouteGeo] = useState(null); // GeoJSON LineString feature
  const [routeMeta, setRouteMeta] = useState(null); // { distanceKm, durationMin }
  const [routeBusy, setRouteBusy] = useState(false);
  const [routeColor, setRouteColor] = useState("#1D4FD7");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    const rgbTriplet = String(
      window.getComputedStyle(root).getPropertyValue("--afrimed-primary") || ""
    ).trim();
    if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(rgbTriplet)) {
      setRouteColor(`rgb(${rgbTriplet})`);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateDesktopFlag = () => setIsDesktopMap(window.innerWidth >= 768);
    updateDesktopFlag();
    window.addEventListener("resize", updateDesktopFlag);
    return () => window.removeEventListener("resize", updateDesktopFlag);
  }, []);

  const initialViewState = useMemo(
    () => ({
      latitude: Number(center?.lat) || 0,
      longitude: Number(center?.lng) || 0,
      zoom: Number(center?.zoom) || 12,
    }),
    [center?.lat, center?.lng, center?.zoom]
  );

  useEffect(() => {
    const tokenPresent = Boolean(String(token || "").trim());
    let webglSupported = false;
    try {
      webglSupported = typeof mapboxgl?.supported === "function"
        ? mapboxgl.supported({ failIfMajorPerformanceCaveat: false })
        : false;
    } catch {
      webglSupported = false;
    }
    const nextSupport = !tokenPresent
      ? "missing-token"
      : webglSupported
        ? "supported"
        : "unsupported";
    setMapSupport(nextSupport);
    setMapState(nextSupport === "supported" ? "loading" : nextSupport === "unsupported" ? "unsupported" : "unavailable");
    recordMapDiagnostic(diagnosticSurface, "support-check", {
      tokenPresent,
      webglSupported,
      ...readSafeMapRuntime(),
    });
  }, [diagnosticSurface, token]);

  const readCurrentBounds = useCallback(() => {
    const mapLike = mapRef.current;
    if (!mapLike) return null;

    const bounds =
      (typeof mapLike.getBounds === "function" && mapLike.getBounds()) ||
      (typeof mapLike.getMap === "function" &&
        typeof mapLike.getMap()?.getBounds === "function" &&
        mapLike.getMap().getBounds()) ||
      null;

    if (!bounds) return null;

    const west =
      typeof bounds.getWest === "function" ? bounds.getWest() : bounds?._sw?.lng;
    const east =
      typeof bounds.getEast === "function" ? bounds.getEast() : bounds?._ne?.lng;
    const south =
      typeof bounds.getSouth === "function" ? bounds.getSouth() : bounds?._sw?.lat;
    const north =
      typeof bounds.getNorth === "function" ? bounds.getNorth() : bounds?._ne?.lat;

    if (![west, east, south, north].every(Number.isFinite)) return null;

    return { west, east, south, north };
  }, []);

  const updateBounds = useCallback(() => {
    const next = readCurrentBounds();
    if (!next) return;
    setMapBounds(next);
    onBoundsChange?.(next);
  }, [onBoundsChange, readCurrentBounds]);

  const clearMapWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const resizeMap = useCallback((reason = "resize") => {
    const frame = frameRef.current;
    const rawMap = getMapboxInstance(mapRef.current);
    if (!frame || !rawMap?.resize) return false;
    const rect = frame.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width < 1 || height < 1) {
      recordMapDiagnostic(diagnosticSurface, "resize-skipped", { reason, width, height });
      return false;
    }
    try {
      rawMap.resize();
      recordMapDiagnostic(diagnosticSurface, "resize", { reason, width, height });
      return true;
    } catch (error) {
      recordMapDiagnostic(diagnosticSurface, "resize-error", {
        reason,
        category: classifyMapError(error),
        message: sanitiseMapMessage(error?.message),
      });
      return false;
    }
  }, [diagnosticSurface]);

  const scheduleResize = useCallback((reason) => {
    if (typeof window === "undefined") return;
    if (resizeFrameRef.current) window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeMap(reason);
    });
  }, [resizeMap]);

  const markMapReady = useCallback((stage) => {
    mapReachedRenderRef.current = true;
    mapReadyRef.current = true;
    clearMapWatchdog();
    setMapState("ready");
    recordMapDiagnostic(diagnosticSurface, stage, {
      elapsedMs: Math.round(performance.now() - mapStartedAtRef.current),
      doctorMarkerCount: Math.min(doctors.length, MAX_VISIBLE_DOCTOR_MARKERS),
      facilityMarkerCount: Math.min(facilities.length, MAX_VISIBLE_FACILITY_MARKERS),
      ...readSafeMapRuntime(),
    });
  }, [MAX_VISIBLE_DOCTOR_MARKERS, MAX_VISIBLE_FACILITY_MARKERS, clearMapWatchdog, diagnosticSurface, doctors.length, facilities.length]);

  const enterMapUnavailable = useCallback((category, error) => {
    clearMapWatchdog();
    setMapState("unavailable");
    recordMapDiagnostic(diagnosticSurface, "fallback", {
      category: category || "unknown",
      message: sanitiseMapMessage(error?.message || error),
      ...readSafeMapRuntime(),
    });
  }, [clearMapWatchdog, diagnosticSurface]);

  const recoverMap = useCallback((reason, error) => {
    if (mapSupport !== "supported") {
      enterMapUnavailable(reason || "webgl", error);
      return;
    }
    const attempts = recoveryAttemptsRef.current;
    if (attempts >= MAX_MAP_RECOVERY_ATTEMPTS) {
      enterMapUnavailable(reason || "unknown", error);
      return;
    }
    recoveryAttemptsRef.current += 1;
    setMapState("loading");
    recordMapDiagnostic(diagnosticSurface, "recovery-attempt", {
      reason,
      attempt: recoveryAttemptsRef.current,
    });
    scheduleResize(`recovery-${reason || "unknown"}`);
    if (recoveryRef.current) window.clearTimeout(recoveryRef.current);
    recoveryRef.current = window.setTimeout(() => {
      if (mapReadyRef.current) return;
      setMapInstanceKey((value) => value + 1);
      recordMapDiagnostic(diagnosticSurface, "controlled-remount", {
        reason,
        attempt: recoveryAttemptsRef.current,
      });
    }, 750);
  }, [diagnosticSurface, enterMapUnavailable, mapSupport, scheduleResize]);

  const armMapWatchdog = useCallback(() => {
    clearMapWatchdog();
    if (typeof document === "undefined" || document.visibilityState !== "visible") return;
    watchdogRef.current = window.setTimeout(() => {
      if (mapReadyRef.current || document.visibilityState !== "visible") return;
      recordMapDiagnostic(diagnosticSurface, "watchdog-timeout", { timeoutMs: MAP_READY_TIMEOUT_MS });
      recoverMap("watchdog");
    }, MAP_READY_TIMEOUT_MS);
  }, [clearMapWatchdog, diagnosticSurface, recoverMap]);

  const detachMapListeners = useCallback(() => {
    mapListenerCleanupRef.current?.();
    mapListenerCleanupRef.current = () => {};
  }, []);

  const attachMapListeners = useCallback((mapLike) => {
    detachMapListeners();
    const rawMap = getMapboxInstance(mapLike);
    const canvas = rawMap?.getCanvas?.();
    if (!rawMap || !canvas) return;

    const onError = (event) => {
      const error = event?.error || event;
      const category = navigator.onLine === false ? "network_offline" : classifyMapError(error);
      recordMapDiagnostic(diagnosticSurface, "mapbox-error", {
        category,
        message: sanitiseMapMessage(error?.message || error),
      });
      if (!mapReadyRef.current) recoverMap(category, error);
    };
    const onContextLost = (event) => {
      event?.preventDefault?.();
      recordMapDiagnostic(diagnosticSurface, "webgl-context-lost", readSafeMapRuntime());
      recoverMap("webgl_context_lost");
    };
    const onContextRestored = () => {
      recordMapDiagnostic(diagnosticSurface, "webgl-context-restored", readSafeMapRuntime());
      scheduleResize("webgl-context-restored");
      armMapWatchdog();
    };

    rawMap.on?.("error", onError);
    canvas.addEventListener("webglcontextlost", onContextLost, false);
    canvas.addEventListener("webglcontextrestored", onContextRestored, false);
    mapListenerCleanupRef.current = () => {
      rawMap.off?.("error", onError);
      canvas.removeEventListener("webglcontextlost", onContextLost, false);
      canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
    };
  }, [armMapWatchdog, detachMapListeners, diagnosticSurface, recoverMap, scheduleResize]);

  const handleMapLoad = useCallback((event) => {
    const mapLike = event?.target || mapRef.current;
    attachMapListeners(mapLike);
    updateBounds();
    if (typeof onMapReady === "function") onMapReady(mapLike);
    const elapsedMs = Math.round(performance.now() - mapStartedAtRef.current);
    recordMapDiagnostic(diagnosticSurface, "map-load", { elapsedMs, ...readSafeMapRuntime() });
    recordMapDiagnostic(diagnosticSurface, "style-load", { elapsedMs, ...readSafeMapRuntime() });
    scheduleResize("map-load");
  }, [attachMapListeners, diagnosticSurface, onMapReady, scheduleResize, updateBounds]);

  const handleMapError = useCallback((event) => {
    const error = event?.error || event;
    const category = navigator.onLine === false ? "network_offline" : classifyMapError(error);
    recordMapDiagnostic(diagnosticSurface, "mapbox-error", {
      category,
      message: sanitiseMapMessage(error?.message || error),
    });
    if (!mapReadyRef.current) recoverMap(category, error);
  }, [diagnosticSurface, recoverMap]);

  const handleMapRender = useCallback(() => {
    if (mapReachedRenderRef.current) return;
    mapReachedRenderRef.current = true;
    recordMapDiagnostic(diagnosticSurface, "first-render", {
      elapsedMs: Math.round(performance.now() - mapStartedAtRef.current),
      ...readSafeMapRuntime(),
    });
  }, [diagnosticSurface]);

  const handleMapIdle = useCallback(() => {
    if (mapReachedIdleRef.current) return;
    mapReachedIdleRef.current = true;
    markMapReady("map-idle");
  }, [diagnosticSurface, markMapReady]);

  const retryMap = useCallback(() => {
    recoveryAttemptsRef.current = 0;
    mapReachedRenderRef.current = false;
    mapReachedIdleRef.current = false;
    mapReadyRef.current = false;
    mapStartedAtRef.current = performance.now();
    setMapState("loading");
    setMapInstanceKey((value) => value + 1);
    recordMapDiagnostic(diagnosticSurface, "manual-retry", readSafeMapRuntime());
  }, [diagnosticSurface]);

  useEffect(() => {
    if (mapSupport !== "supported") return undefined;
    mapReachedRenderRef.current = false;
    mapReachedIdleRef.current = false;
    mapReadyRef.current = false;
    mapStartedAtRef.current = performance.now();
    setMapState("loading");
    recordMapDiagnostic(diagnosticSurface, "mount", {
      elapsedMs: 0,
      tokenPresent: Boolean(String(token || "").trim()),
      ...readSafeMapRuntime(),
    });
    armMapWatchdog();
    return () => clearMapWatchdog();
  }, [armMapWatchdog, clearMapWatchdog, diagnosticSurface, mapInstanceKey, mapSupport, token]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onVisibility = () => {
      recordMapDiagnostic(diagnosticSurface, "visibility-change", readSafeMapRuntime());
      if (document.visibilityState === "visible") {
        scheduleResize("visibility");
        if (!mapReadyRef.current) armMapWatchdog();
      } else {
        clearMapWatchdog();
      }
    };
    const onPageShow = () => {
      recordMapDiagnostic(diagnosticSurface, "pageshow", readSafeMapRuntime());
      scheduleResize("pageshow");
      if (!mapReadyRef.current) armMapWatchdog();
    };
    const onViewportChange = () => scheduleResize("viewport");
    const observer = typeof ResizeObserver === "undefined" || !frameRef.current
      ? null
      : new ResizeObserver(() => scheduleResize("container"));
    observer?.observe(frameRef.current);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("orientationchange", onViewportChange);
    window.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("resize", onViewportChange);
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("orientationchange", onViewportChange);
      window.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      if (resizeFrameRef.current) window.cancelAnimationFrame(resizeFrameRef.current);
      if (recoveryRef.current) window.clearTimeout(recoveryRef.current);
      detachMapListeners();
    };
  }, [armMapWatchdog, clearMapWatchdog, detachMapListeners, diagnosticSurface, scheduleResize]);

  useEffect(() => {
    // Sync initial bounds once the map has been mounted.
    const t = window.setTimeout(updateBounds, 0);
    return () => window.clearTimeout(t);
  }, [updateBounds, facilities?.length, doctors?.length]);

  const centerPoint = useMemo(() => {
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : { lat: 0, lng: 0 };
  }, [center?.lat, center?.lng]);

  useEffect(() => {
    const mapLike = mapRef.current;
    if (!mapLike) return;
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);
    if (!isValidLatLng(lat, lng)) return;
    const zoom = Number(center?.zoom);
    const nextZoom = Number.isFinite(zoom) ? zoom : undefined;
    const payload = {
      center: [lng, lat],
      duration: Number(center?.duration ?? 650),
      ...(nextZoom != null ? { zoom: nextZoom } : {}),
    };
    if (typeof mapLike.flyTo === "function") {
      mapLike.flyTo(payload);
      return;
    }
    const rawMap = typeof mapLike.getMap === "function" ? mapLike.getMap() : null;
    rawMap?.flyTo?.(payload);
  }, [center?.duration, center?.lat, center?.lng, center?.zoom, isValidLatLng]);

  const inLngRange = useCallback((lng, b) => {
    if (!Number.isFinite(lng) || !b) return false;
    // Dateline-aware check.
    if (b.west <= b.east) return lng >= b.west && lng <= b.east;
    return lng >= b.west || lng <= b.east;
  }, []);

  const pickVisiblePoints = useCallback((points = [], maxCount = 200) => {
    const list = Array.isArray(points) ? points : [];
    if (!list.length) return [];
    if (!mapBounds) {
      // Before bounds are ready, pick points nearest to center instead of first rows.
      return list
        .slice()
        .map((p) => {
          const lat = Number(p?.lat);
          const lng = Number(p?.lng);
          if (!isValidLatLng(lat, lng)) {
            return { p, d: Number.POSITIVE_INFINITY };
          }
          const dLat = Number.isFinite(lat) ? lat - centerPoint.lat : Number.POSITIVE_INFINITY;
          const dLng = Number.isFinite(lng) ? lng - centerPoint.lng : Number.POSITIVE_INFINITY;
          return { p, d: dLat * dLat + dLng * dLng };
        })
        .sort((a, b) => a.d - b.d)
        .slice(0, maxCount)
        .map((x) => x.p);
    }

    const visible = list.filter((p) => {
      const lat = Number(p?.lat);
      const lng = Number(p?.lng);
      if (!isValidLatLng(lat, lng)) return false;
      if (lat < mapBounds.south || lat > mapBounds.north) return false;
      return inLngRange(lng, mapBounds);
    });

    if (visible.length <= maxCount) return visible;

    const step = Math.ceil(visible.length / maxCount);
    const sampled = [];
    for (let i = 0; i < visible.length; i += step) {
      sampled.push(visible[i]);
      if (sampled.length >= maxCount) break;
    }
    return sampled;
  }, [centerPoint.lat, centerPoint.lng, inLngRange, isValidLatLng, mapBounds]);

  const visibleFacilities = useMemo(
    () => pickVisiblePoints(facilities, MAX_VISIBLE_FACILITY_MARKERS),
    [facilities, pickVisiblePoints]
  );

  const visibleDoctors = useMemo(
    () => pickVisiblePoints(doctors, MAX_VISIBLE_DOCTOR_MARKERS),
    [doctors, pickVisiblePoints]
  );

  const positionPopupForPayload = useCallback((payload) => {
    if (!isDesktopMap) {
      setPopupPosition(null);
      return;
    }

    const lat = Number(payload?.lat ?? payload?.latitude);
    const lng = Number(payload?.lng ?? payload?.longitude);
    const frame = frameRef.current;
    const mapLike = mapRef.current;
    if (!frame || !mapLike || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setPopupPosition(null);
      return;
    }

    const project =
      typeof mapLike.project === "function"
        ? mapLike.project.bind(mapLike)
        : typeof mapLike.getMap === "function" &&
            typeof mapLike.getMap()?.project === "function"
          ? mapLike.getMap().project.bind(mapLike.getMap())
          : null;

    if (!project) {
      setPopupPosition(null);
      return;
    }

    const point = project([lng, lat]);
    const x = Number(point?.x ?? point?.[0]);
    const y = Number(point?.y ?? point?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      setPopupPosition(null);
      return;
    }

    const frameRect = frame.getBoundingClientRect();
    const cardWidth = 292;
    const cardHeight = 150;
    const gutter = 12;
    const minTop = 92;
    const maxLeft = Math.max(gutter, frameRect.width - cardWidth - gutter);
    const maxTop = Math.max(minTop, frameRect.height - cardHeight - gutter);
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const aboveMarker = y - cardHeight - 18;
    const belowMarker = y + 24;
    const preferredTop = aboveMarker >= minTop ? aboveMarker : belowMarker;

    setPopupPosition({
      left: clamp(x - cardWidth / 2, gutter, maxLeft),
      top: clamp(preferredTop, minTop, maxTop),
    });
  }, [isDesktopMap]);

  useEffect(() => {
    if (popup) positionPopupForPayload(popup);
  }, [isDesktopMap, popup, positionPopupForPayload]);

  const closeActivePopup = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();

    const closing = popup;
    const closingKey = String(closing?.id || "").trim() || "__popup__";
    closingPopupIdRef.current = closingKey;
    setPopup(null);
    setSelectedMarkerId(null);
    setPopupPosition(null);
    if (
      closing?.type === "facility" &&
      typeof onFacilityPopupClose === "function"
    ) {
      onFacilityPopupClose(closing);
    }
    window.setTimeout(() => {
      if (closingPopupIdRef.current === closingKey) {
        closingPopupIdRef.current = null;
      }
    }, 220);
  }, [onFacilityPopupClose, popup]);

  const onMarkerClick = useCallback((e, markerId, payload) => {
  e?.originalEvent?.stopPropagation?.();
  e?.preventDefault?.();
  e?.stopPropagation?.();
  e?.nativeEvent?.stopImmediatePropagation?.();

  // ensure popup always has lat/lng for geocode + display
  const p = {
    ...payload,
    lat: payload?.lat ?? payload?.latitude ?? null,
    lng: payload?.lng ?? payload?.longitude ?? null,
  };

  setSelectedMarkerId(markerId);
  setPopup(p);
  positionPopupForPayload(p);

  // Keep facility address grounded in source data; only geocode non-facility markers.
  if (String(p?.type || "").toLowerCase() !== "facility") {
    hydratePopupAddress(p);
  }
}, [positionPopupForPayload]);

  const stopPopupCloseEvent = useCallback((event) => {
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
  }, []);

  const closePopupFromControl = useCallback((event) => {
    closeActivePopup(event);
  }, [closeActivePopup]);

  useEffect(() => {
    const lat = Number(focusTarget?.lat);
    const lng = Number(focusTarget?.lng);
    if (!isValidLatLng(lat, lng)) return;
    const focusKey = String(focusTarget?.id || "").trim() || "__popup__";
    if (closingPopupIdRef.current && closingPopupIdRef.current === focusKey) return;

    const nextPopup = {
      type: "facility",
      id: focusTarget?.id ?? null,
      name: normalizeFacilityName(focusTarget),
      address: normalizeFacilityAddress(focusTarget),
      lat,
      lng,
    };
    setPopup(nextPopup);
    positionPopupForPayload(nextPopup);
  }, [focusTarget, isValidLatLng, normalizeFacilityAddress, normalizeFacilityName, positionPopupForPayload]);

  const onIconError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src =
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 28'>
           <circle cx='14' cy='14' r='10' fill='#2563eb' stroke='white' stroke-width='3'/>
         </svg>`
      );
  };

  const nearestFacilityId = useMemo(() => {
    let best = null;
    let bestD = Infinity;
    for (const f of visibleFacilities || []) {
      if (f?.lat == null || f?.lng == null) continue;
      const dLat = Number(f.lat) - centerPoint.lat;
      const dLng = Number(f.lng) - centerPoint.lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) {
        bestD = d;
        best = f?.id ?? null;
      }
    }
    return best;
  }, [visibleFacilities, centerPoint.lat, centerPoint.lng]);

  const nearestDoctorId = useMemo(() => {
    let best = null;
    let bestD = Infinity;
    for (const d0 of visibleDoctors || []) {
      if (d0?.lat == null || d0?.lng == null) continue;
      const dLat = Number(d0.lat) - centerPoint.lat;
      const dLng = Number(d0.lng) - centerPoint.lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) {
        bestD = d;
        best = d0?.id ?? null;
      }
    }
    return best;
  }, [visibleDoctors, centerPoint.lat, centerPoint.lng]);

  // ✅ In-app directions (draw route line)
  const getDirections = useCallback(
    async (from, to) => {
      const fromLat = Number(from?.lat);
      const fromLng = Number(from?.lng);
      const toLat = Number(to?.lat);
      const toLng = Number(to?.lng);

      if (!token) return;
      if (![fromLat, fromLng, toLat, toLng].every(Number.isFinite)) return;

      setRouteBusy(true);
      try {
        const url =
          `https://api.mapbox.com/directions/v5/mapbox/driving/` +
          `${fromLng},${fromLat};${toLng},${toLat}` +
          `?geometries=geojson&overview=full&access_token=${encodeURIComponent(token)}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Directions failed");
        const json = await res.json();

        const route = json?.routes?.[0];
        const coords = route?.geometry?.coordinates;

        if (!route || !Array.isArray(coords) || coords.length < 2) return;

        setRouteGeo({
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: coords,
          },
        });

        const distanceKm = (Number(route.distance) || 0) / 1000;
        const durationMin = (Number(route.duration) || 0) / 60;
        setRouteMeta({
          distanceKm: Number.isFinite(distanceKm) ? distanceKm : null,
          durationMin: Number.isFinite(durationMin) ? durationMin : null,
        });
        setPopup(null);
        setSelectedMarkerId(null);
        setPopupPosition(null);

        // Fly to route start-ish (nice UX)
        mapRef.current?.flyTo?.({
          center: [toLng, toLat],
          zoom: 13.5,
          duration: 800,
        });
      } catch {
        // silent by design (no red)
      } finally {
        setRouteBusy(false);
      }
    },
    [token]
  );

  const clearRoute = useCallback(() => {
    setRouteGeo(null);
    setRouteMeta(null);
  }, []);

  const openMarkerPopup = useCallback((event, markerId, payload, afterOpen) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
    suppressNextMapClickRef.current = true;
    window.setTimeout(() => {
      suppressNextMapClickRef.current = false;
    }, 180);
    onMarkerClick(event, markerId, payload);
    if (typeof afterOpen === "function") afterOpen();
  }, [onMarkerClick]);

  const routeSource = useMemo(() => {
    if (!routeGeo) return null;
    return {
      type: "FeatureCollection",
      features: [routeGeo],
    };
  }, [routeGeo]);

  const routeLayer = useMemo(
    () => ({
      id: "afrimed-route-line",
      type: "line",
      source: "afrimed-route",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": routeColor,
        "line-width": 5,
        "line-opacity": 0.92,
      },
    }),
    [routeColor]
  );

  const routeEndpoints = useMemo(() => {
    const coords = routeGeo?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;

    const first = coords[0] || [];
    const last = coords[coords.length - 1] || [];
    const start = {
      lng: Number(first[0]),
      lat: Number(first[1]),
    };
    const end = {
      lng: Number(last[0]),
      lat: Number(last[1]),
    };

    if (!isValidLatLng(start.lat, start.lng) || !isValidLatLng(end.lat, end.lng)) {
      return null;
    }

    return { start, end };
  }, [isValidLatLng, routeGeo]);

  const hasRouteOrigin =
    Number.isFinite(Number(user?.lat)) &&
    Number.isFinite(Number(user?.lng));
  const hasRouteDestination =
    popup?.type === "facility" &&
    Number.isFinite(Number(popup?.lat)) &&
    Number.isFinite(Number(popup?.lng));
  const canRoute = hasRouteDestination;
  const popupOverlayStyle =
    popupPosition && isDesktopMap
      ? {
          left: popupPosition.left,
          top: popupPosition.top,
          right: "auto",
          justifyContent: "flex-start",
        }
      : undefined;

  return (
    <div
      ref={frameRef}
      className={`relative ${frameClassName} ${heightClass}`}
      data-testid="doctor-map-frame"
      data-map-state={mapState}
    >
      {mapSupport === "supported" ? (
      <MapGL
        key={`${mapStyle}-${mapInstanceKey}`}
        ref={mapRef}
        initialViewState={initialViewState}
        mapboxAccessToken={token}
        mapStyle={mapStyle}
        style={{ width: "100%", height: "100%" }}
        onLoad={handleMapLoad}
        onError={handleMapError}
        onRender={handleMapRender}
        onIdle={handleMapIdle}
        onMoveEnd={() => {
          updateBounds();
          if (popup) positionPopupForPayload(popup);
        }}
        onClick={(event) => {
          if (suppressNextMapClickRef.current) {
            suppressNextMapClickRef.current = false;
            return;
          }
          if (!activeMapPopup) return;
          closeActivePopup(event?.originalEvent || event);
        }}
      >
        {/* ✅ Route line (in-app directions) */}
        {routeSource ? (
          <Source id="afrimed-route" type="geojson" data={routeSource}>
            <Layer {...routeLayer} />
          </Source>
        ) : null}

        {/* Route endpoints (blue origin + drop-pin style destination) */}
        {routeEndpoints?.start ? (
          <Marker
            latitude={routeEndpoints.start.lat}
            longitude={routeEndpoints.start.lng}
            anchor="center"
          >
            <div
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                borderRadius: "9999px",
                background: "rgb(var(--afrimed-primary))",
                border: "3px solid rgba(255,255,255,0.95)",
                boxShadow: "0 2px 8px rgba(15,23,42,0.25)",
              }}
            />
          </Marker>
        ) : null}

        {routeEndpoints?.end ? (
          <Marker
            latitude={routeEndpoints.end.lat}
            longitude={routeEndpoints.end.lng}
            anchor="bottom"
          >
            <img
              src={facilityIconNearest || facilityIcon}
              alt=""
              draggable="false"
              onError={onIconError}
              style={{
                width: 34,
                height: 34,
                objectFit: "contain",
              }}
            />
          </Marker>
        ) : null}

        {/* Facilities */}
        {(visibleFacilities || []).map((f) => {
          const isNearest = f?.id != null && f.id === nearestFacilityId;
          const isHighlight = f?.id != null && f.id === highlightFacilityId;

          const icon = isNearest ? facilityIconNearest : facilityIcon;
          const size = isNearest ? facilityNearestSize : facilitySize;

          return (
            <Marker
              key={`fac-${f.id}`}
              latitude={Number(f.lat)}
              longitude={Number(f.lng)}
              anchor="bottom"
            >
              <button
                type="button"
                onPointerUp={(e) => {
                  openMarkerPopup(e, `facility:${f.id}`, {
                    type: "facility",
                    ...f,
                    name: normalizeFacilityName(f),
                    address: normalizeFacilityAddress(f),
                    city: String(f?.city || f?.facility_city || "").trim(),
                    country: String(f?.country || f?.facility_country || "").trim(),
                  }, () => {
                    if (typeof onFacilitySelect === "function") onFacilitySelect(f);
                  });
                }}
                onClick={stopPopupCloseEvent}
                className={isHighlight ? "animate-bounce" : ""}
                style={{
                  width: size,
                  height: size,
                  display: "grid",
                  placeItems: "center",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  transform: isHighlight ? "scale(1.06)" : "scale(1)",
                  transition: "transform 180ms ease",
                }}
                aria-label={f?.name ? `Open ${f.name}` : "Open facility"}
              >
                <img
                  src={icon}
                  onError={onIconError}
                  width={size}
                  height={size}
                  alt=""
                  draggable="false"
                  style={{
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </button>
            </Marker>
          );
        })}

        {/* Doctors */}
        {(visibleDoctors || []).map((d) => {
          const isNearest = d?.id != null && d.id === nearestDoctorId;
          const icon = isNearest ? doctorIconNearest : doctorIcon;
          const size = isNearest ? doctorNearestSize : doctorSize;

          return (
            <Marker
              key={`doc-${d.id}`}
              latitude={Number(d.lat)}
              longitude={Number(d.lng)}
              anchor="bottom"
            >
              <button
                type="button"
                onPointerUp={(e) => {
                  openMarkerPopup(e, `doctor:${d.id}`, {
                    type: "doctor",
                    name: d.name,
                    ...d,
                  });
                }}
                onClick={stopPopupCloseEvent}
                style={{
                  width: size,
                  height: size,
                  display: "grid",
                  placeItems: "center",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
                aria-label={d?.name ? `Open ${d.name}` : "Open doctor"}
              >
                <img
                  src={icon}
                  onError={onIconError}
                  width={size}
                  height={size}
                  alt=""
                  draggable="false"
                  style={{
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </button>
            </Marker>
          );
        })}

      {/* User marker: preserve uploaded SVG appearance + pulse */}
      {showUser && user?.lat != null && user?.lng != null && (
        <Marker latitude={Number(user.lat)} longitude={Number(user.lng)} anchor="bottom">
          <button
            type="button"
            onPointerUp={(e) => {
              openMarkerPopup(e, "user", { type: "you", name: "You", ...user });
            }}
            onClick={stopPopupCloseEvent}
            style={{
              position: "relative",
              width: userSize + 20,
              height: userSize + 20,
              display: "grid",
              placeItems: "center",
              border: 0,
              padding: 0,
              margin: 0,
              background: "transparent",
              cursor: "pointer",
              pointerEvents: "auto",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
            aria-label="You"
          >
            {/* Pulse ring behind icon */}
            <span
              style={{
                position: "absolute",
                width: userSize + 20,
                height: userSize + 20,
                borderRadius: 999,
                background: "rgba(0, 189, 196, 0.16)",
                animation: "afrimedPulse 1.6s ease-out infinite",
                pointerEvents: "none",
              }}
            />

            <img
              src={userIcon}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              width={userSize}
              height={userSize}
              alt=""
              draggable="false"
              style={{
                position: "relative",
                width: userSize,
                height: userSize,
                objectFit: "contain",
                pointerEvents: "none",
                userSelect: "none",
              }}
            />

            <style>{`
              @keyframes afrimedPulse {
                0%   { transform: scale(0.72); opacity: 0.90; }
                100% { transform: scale(1.28); opacity: 0; }
              }
            `}</style>
          </button>
        </Marker>
      )}

      </MapGL>
      ) : null}

      {mapState !== "ready" ? (
        <MapStatus state={mapState} onRetry={retryMap} t={t} />
      ) : null}

      {popup ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-[112px] z-[90] flex justify-center px-5 md:inset-auto md:px-0"
          style={popupOverlayStyle}
        >
          <div
            className="pointer-events-auto relative w-full max-w-[252px] rounded-[14px] border border-[rgba(24,50,119,0.08)] bg-[rgb(var(--clr-surface-cont-highest))] px-3.5 pb-3.5 pt-3 text-on-surface shadow-[0_16px_34px_rgba(4,8,18,0.20)] sm:max-w-[292px]"
            style={{ touchAction: "manipulation" }}
            onPointerDown={stopPopupCloseEvent}
            onClick={stopPopupCloseEvent}
          >
            <button
              type="button"
              onPointerDown={closePopupFromControl}
              onClick={closePopupFromControl}
              className="absolute right-2 top-2 z-[2] flex h-8 w-8 items-center justify-center rounded-full text-on-surface-var"
              style={{ pointerEvents: "auto", touchAction: "manipulation" }}
              aria-label="Close"
            >
              <img
                src="/icons/pharmacy/circle-x.svg"
                alt=""
                aria-hidden="true"
                className="h-6 w-6"
                draggable="false"
              />
            </button>

            <div className="pr-8 text-body-large font-medium leading-snug text-on-surface">
              {popup.name || "Healthcare facility"}
            </div>
            {popup.address ? (
              <div className="mt-1 pr-8 text-body-small leading-snug text-on-surface-var">
                {popup.address}
              </div>
            ) : null}

            {canRoute ? (
              <div className="mt-3 space-y-2">
                {hasRouteOrigin &&
                (routeMeta?.distanceKm != null || routeMeta?.durationMin != null) ? (
                  <div className="text-body-small text-on-surface-var">
                    {routeMeta?.distanceKm != null ? `${routeMeta.distanceKm.toFixed(1)} km` : ""}
                    {routeMeta?.durationMin != null
                      ? ` · ${Math.round(routeMeta.durationMin)} min`
                      : ""}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => {
                    if (hasRouteOrigin) {
                      void getDirections(user, popup);
                      return;
                    }
                    if (typeof onRequireLocation === "function") {
                      onRequireLocation();
                    }
                  }}
                  disabled={routeBusy}
                  className="mx-auto flex h-10 min-w-[150px] items-center justify-center rounded-[12px] border border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface))] px-4 py-2 text-center text-label-large whitespace-nowrap text-on-surface shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition hover:bg-[rgba(77,163,255,0.12)] disabled:cursor-default disabled:opacity-60"
                >
                  {routeBusy ? "Getting route…" : "Take me there"}
                </button>

                {routeGeo ? (
                  <button
                    type="button"
                    onClick={clearRoute}
                    className="mx-auto flex h-11 min-w-[148px] items-center justify-center rounded-[12px] bg-[rgba(91,107,134,0.10)] px-5 py-2.5 text-center text-label-large whitespace-nowrap text-on-surface"
                  >
                    Clear route
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {routeGeo && !popup ? (
        <div className="pointer-events-none absolute right-4 top-[164px] z-[90]">
          <button
            type="button"
            onClick={clearRoute}
            className="pointer-events-auto flex h-10 items-center justify-center rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 text-label-large whitespace-nowrap text-on-surface shadow-[0_10px_24px_rgba(4,8,18,0.18)]"
            style={{ touchAction: "manipulation" }}
          >
            Clear route
          </button>
        </div>
      ) : null}
    </div>
  );
}
